import { Dispatch, SetStateAction } from "react";
import { CustomTable as Table } from "./CustomTable";
import styled from "styled-components";
import { Filter } from "./Filter";

interface ICustomTable {
  columns: any;
  data?: any;
  isLoading?: any;
  navigation?: any;
  tableType?: string;
  actionButtonsConfig?: any[];
  actionRightButtons?: any[];
  actionRightAccessories?: any;
  footerCenterContent?: any;
  setPage?: Dispatch<SetStateAction<number>>;
  dataSize?: number;
  searchByPage?: boolean;
  setFilterValues?: Dispatch<SetStateAction<any>>;
  totalAmountOfRecords?: number;
  setOpenFilters?: Dispatch<SetStateAction<boolean>>;
  openFilters?: boolean;
  loading?: boolean;
  asyncConfig?: any;
  pageSize?: number;
  selectConfig?: any;
  hideSelectionBar?: boolean;
}

const ResponsiveWrapper = styled.div<{ hideSelectionBar?: boolean }>`
  height: calc(100vh - 200px);
  max-height: calc(100vh - 200px);
  position: relative;
  overflow: hidden;
  border-radius: 1rem;
  margin-bottom: 2rem;

  /* Target the table header bar produced by the UI lib */
  @media (max-width: 1440px) {
    .flex.gap-2.w-full.bg-neutral-0,
    .flex.gap-2.w-full.bg-neutral-0.dark\\:bg-neutral-900 {
      flex-wrap: wrap;
      align-items: center;
      row-gap: 0.5rem;
      gap: 0.75rem;
    }
  }

  @media (max-width: 960px) {
    .flex.gap-2.w-full.bg-neutral-0 > .flex.grow.items-center,
    .flex.gap-2.w-full.bg-neutral-0.dark\\:bg-neutral-900
      > .flex.grow.items-center {
      width: 100%;
      display: flex;
      justify-content: center;
    }
    /* Shrink the search input */
    .flex.gap-2.w-full.bg-neutral-0 input.h-12,
    .flex.gap-2.w-full.bg-neutral-0.dark\\:bg-neutral-900 input.h-12 {
      width: min(100%, 28rem) !important;
    }
    .flex.gap-2.w-full.bg-neutral-0
      > .flex.gap-2.align-end.justify-center.items-center,
    .flex.gap-2.w-full.bg-neutral-0.dark\\:bg-neutral-900
      > .flex.gap-2.align-end.justify-center.items-center {
      width: 100%;
      display: flex;
      justify-content: center;
      flex-wrap: wrap;
    }
    .flex.gap-2.w-full.bg-neutral-0 .w-52,
    .flex.gap-2.w-full.bg-neutral-0.dark\\:bg-neutral-900 .w-52 {
      min-width: 12rem;
      max-width: 16rem;
    }
  }

  @media (max-width: 500) {
    .flex.gap-2.w-full.bg-neutral-0
      > .flex.gap-2.align-end.justify-center.items-center,
    .flex.gap-2.w-full.bg-neutral-0.dark\\:bg-neutral-900
      > .flex.gap-2.align-end.justify-center.items-center {
      flex-direction: column;
      align-items: stretch;
    }
    /* Further shrink search input and reduce padding */
    .flex.gap-2.w-full.bg-neutral-0 input.h-12,
    .flex.gap-2.w-full.bg-neutral-0.dark\\:bg-neutral-900 input.h-12 {
      width: min(100%, 20rem) !important;
      height: 2.5rem;
      padding-top: 0.5rem;
      padding-bottom: 0.5rem;
    }
    .flex.gap-2.w-full.bg-neutral-0 .w-52,
    .flex.gap-2.w-full.bg-neutral-0.dark\\:bg-neutral-900 .w-52 {
      width: 100%;
      min-width: 0;
      max-width: 100%;
    }
  }

  /* Optionally hide the built-in BulkButtons bar from the UI library */
  ${(props) =>
    props.hideSelectionBar &&
    `
    .inside-table .flex.py-1.px-4.gap-4.sticky.z-10.bg-neutral-0.dark\\:bg-neutral-900.top-0.items-center.justify-between {
      display: none !important;
    }
    /* Ensure table header does not shift down when selection is active */
    .inside-table thead.sticky {
      top: 0 !important;
    }
  `}
`;

const FooterOverlay = styled.div`
  position: absolute;
  bottom: 0.75rem;
  left: 50%;
  transform: translateX(-50%);
  pointer-events: none;
  font-size: 0.875rem;
  color: #6b7280; /* neutral-500 */
`;

export const CoreTable = ({
  columns,
  data,
  navigation,
  tableType,
  setFilterValues,
  actionButtonsConfig = [],
  actionRightButtons,
  actionRightAccessories,
  setOpenFilters = () => {},
  setPage = () => {},
  openFilters,
  loading,
  asyncConfig,
  pageSize = 50,
  selectConfig,
  hideSelectionBar,
  footerCenterContent,
  ...config
}: ICustomTable) => {
  return (
    <>
      <Filter
        setFilterValues={setFilterValues}
        columns={columns}
        data={data}
        tableType={tableType}
        setOpenFilters={setOpenFilters}
        onPaginationChange={asyncConfig?.onPaginationChange}
        openFilters={openFilters}
        currentPageIndex={asyncConfig?.pageIndex}
        currentPageSize={asyncConfig?.pageSize}
      />
      <ResponsiveWrapper hideSelectionBar={hideSelectionBar}>
        <Table
          columns={columns.filter((c) => c.visible !== false)}
          data={data}
          initialTableState={{
            pagination: {
              pageSize: pageSize,
              pageIndex: 0,
            },
          }}
          handleRowClick={() => navigation()}
          actionButtonsConfig={actionButtonsConfig}
          actionRightButtons={actionRightButtons}
          actionRightAccessories={actionRightAccessories}
          loading={loading}
          asyncConfig={asyncConfig}
          selectConfig={selectConfig}
          {...config}
        />
        {footerCenterContent && (
          <FooterOverlay>{footerCenterContent}</FooterOverlay>
        )}
      </ResponsiveWrapper>
    </>
  );
};
